import { ChangeDetectionStrategy, Component, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { IconComponent } from '@kin/spark-ui';
import { DirectQuoteStore } from '../direct-quote/direct-quote.store';
import { BIRTH_DATE_PLACEHOLDER, formatBirthDate, parseBirthDate } from '../direct-quote/birth-date';

/**
 * The /enrolled date of birth for the direct quote, as masked MM/DD/YYYY text (not a native date picker, so typing
 * and autofill work everywhere). Checked and saved to DirectQuoteStore on blur; an empty
 * field is only flagged on submit (see `commit`), so tabbing past it doesn't raise an error.
 */
@Component({
  selector: 'app-birth-date-field',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; width: 100%; max-width: 280px; margin: 0 0 24px; }
    /* Body Small / 600, like the card's other small type */
    .label { display: block; font-size: 14px; font-weight: 600; line-height: 1.5; color: var(--kin-text); margin-bottom: 6px; }
    /* Same input as the /signup contact fields; tabular figures keep the mask from shifting while typing */
    .input {
      width: 100%; padding: 12px 14px; font-size: 16px; font-family: inherit; color: var(--kin-text);
      font-variant-numeric: tabular-nums; border: 1px solid var(--kin-neutral-300); border-radius: var(--kin-radius-md);
      background: var(--kin-white); box-sizing: border-box;
      transition: border-color 180ms var(--kin-ease-out);
    }
    .input::placeholder { color: var(--kin-text-tertiary); }
    .input:hover { border-color: var(--kin-neutral-500); }
    .input:focus { outline: none; border-color: var(--kin-neutral-1000); box-shadow: var(--kin-shadow-focus); }
    .input.invalid { border-color: var(--kin-red-600); }
    .input[readonly] { color: var(--kin-text-secondary); background: var(--kin-neutral-100); }
    /* Caption in Neutral-600 (4.6:1 on white); the error in Red-600 (7.1:1) */
    .message { display: flex; gap: 6px; margin: 6px 0 0; font-size: 12px; line-height: 1.5; color: var(--kin-text-tertiary); }
    .message.error { color: var(--kin-red-600); font-weight: 500; }
    .message ngk-icon { flex-shrink: 0; margin-top: 2px; }
    @media (max-width: 760px) { :host { max-width: none; } }
    @media (prefers-reduced-motion: reduce) { .input { transition: none; } }
  `,
  template: `
    <label class="label" for="dob">Date of birth</label>
    <input #input id="dob" name="dob" class="input" [class.invalid]="error()" type="text" inputmode="numeric"
      autocomplete="bday" maxlength="10" [placeholder]="placeholder" required [readOnly]="readonly()"
      [value]="text()" [attr.aria-invalid]="error() ? 'true' : null" aria-describedby="dob-message"
      (input)="onInput($event)" (blur)="commit(false)" />
    <!-- One live region: the hint, replaced by the error while there is one -->
    <p id="dob-message" class="message" [class.error]="error()" aria-live="polite">
      @if (error()) {
        <ngk-icon icon="circle-alert" size="14px" aria-hidden="true" /><span>{{ error() }}</span>
      } @else {
        The one thing we don't have. It's used to find discounts you qualify for, not for your property tax protest.
      }
    </p>
  `,
})
export class BirthDateFieldComponent {
  /** Locked while the quote is being started */
  readonly readonly = input(false);

  protected readonly placeholder = BIRTH_DATE_PLACEHOLDER;
  protected readonly text = signal('');
  protected readonly error = signal('');

  private store = inject(DirectQuoteStore);
  private inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');

  /**
   * Checks the entered date and saves it to the store (YYYY-MM-DD), or clears it there and shows why. Reads the input
   * itself, so pressing Enter (submit without a blur) checks what's typed. Empty is an error only when `required`.
   * True when a date was saved.
   */
  commit(required = true): boolean {
    if (this.readonly()) return this.store.dob() !== null;
    const text = formatBirthDate(this.inputRef().nativeElement.value);
    this.text.set(text);
    if (!text && !required) {
      this.store.dob.set(null);
      this.error.set('');
      return false;
    }
    const result = parseBirthDate(text, this.store.state());
    this.store.dob.set(result.iso ?? null);
    this.error.set(result.error ?? '');
    return !result.error;
  }

  focus(): void {
    this.inputRef().nativeElement.focus();
  }

  protected onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    // Padding after a deletion would refill the field ("03/14/1985" → "0/14/1985" → "00/14/1985"), leaving no room
    // for the replacement digit; blur pads whatever is left
    const deleting = (event as InputEvent).inputType?.startsWith('delete') ?? false;
    const next = formatBirthDate(el.value, !deleting);
    if (el.value !== next) {
      // Typing at the end stays at the end (padding "1/" to "01" adds a digit before the caret); a mid-edit keeps
      // the caret after the same digit when the mask adds or drops a slash
      const start = el.selectionStart ?? el.value.length;
      const caret = start >= el.value.length
        ? next.length
        : caretAfterDigits(next, el.value.slice(0, start).replace(/\D/g, '').length);
      el.value = next;
      el.setSelectionRange(caret, caret);
    }
    this.text.set(next);
    // A changed date isn't the saved one until it's checked again
    this.store.dob.set(null);
    this.error.set('');
  }
}

/** The index just after the `count`th digit of `value` (the end when it has fewer). */
function caretAfterDigits(value: string, count: number): number {
  if (count === 0) return 0;
  let seen = 0;
  for (let i = 0; i < value.length; i++) {
    if (/\d/.test(value[i]) && ++seen === count) return i + 1;
  }
  return value.length;
}
